import React, { useMemo } from "react";
import Select from "react-select";
import { useQuery } from "react-query";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const OptionLabel = ({ name, email }) => (
  <div className="hp-assign-opt">
    <span className="hp-assign-avatar">{getInitials(name)}</span>
    <div className="hp-assign-opt-text">
      <span className="hp-assign-opt-name">{name}</span>
      {email && <span className="hp-assign-opt-email">{email}</span>}
    </div>
  </div>
);

const managerSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13,
    borderColor: state.isFocused ? "#4698CB" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(70,152,203,0.15)" : "none",
    borderRadius: 7,
    "&:hover": { borderColor: "#4698CB" },
    cursor: "pointer",
    transition: "border-color 0.15s, box-shadow 0.15s",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: state.isSelected ? "#154068" : state.isFocused ? "#eef4fb" : "transparent",
    color: state.isSelected ? "#fff" : "#0f1b2a",
    cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, fontSize: 13, color: "#94a3b8" }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: 10, fontSize: 13, overflow: "hidden", boxShadow: "0 8px 28px rgba(15,27,42,0.14)", border: "1px solid #eef1f5" }),
  menuList: (base) => ({ ...base, padding: 5 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#94a3b8", padding: "0 8px" }),
  input: (base) => ({ ...base, outline: "none", boxShadow: "none" }),
};

export const ManagerSelect = ({ value, onChange, dataLoader, icon, label, emptyText, placeholder, noOptionsMessage }) => {
  const { data, isLoading } = useQuery(
    [dataLoader.uniqueKey],
    dataLoader.loader,
    { refetchOnWindowFocus: false, retry: false }
  );

  const options = useMemo(
    () =>
      (data?.data || []).map(({ name, email }) => ({
        value: email,
        label: `${name} (${email})`,
        name,
        email,
      })),
    [data]
  );

  const selectedOption = value ? options.find((o) => o.value === value) || { value, name: value, email: value } : null;

  return (
    <div className="hp-assign">
      <div className="hp-assign-head">
        {icon && <span className="hp-assign-icon">{icon}</span>}
        <span className="hp-assign-title">{label} *</span>
      </div>
      <Select
        styles={managerSelectStyles}
        options={options}
        value={null}
        onChange={(opt) => opt && onChange(opt.value)}
        placeholder={placeholder || "Search to add…"}
        isLoading={isLoading}
        controlShouldRenderValue={false}
        isClearable={false}
        formatOptionLabel={(opt) => <OptionLabel name={opt.name} email={opt.email} />}
        noOptionsMessage={() => noOptionsMessage || "No results found"}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      <div className="hp-assign-list">
        {!selectedOption ? (
          <div className="hp-assign-empty">{emptyText || `No ${label.toLowerCase()} selected yet`}</div>
        ) : (
          <div className="hp-assign-person">
            <span className="hp-assign-avatar">{getInitials(selectedOption.name)}</span>
            <div className="hp-assign-person-info">
              <span className="hp-assign-name">{selectedOption.name}</span>
              {selectedOption.email !== selectedOption.name && (
                <span className="hp-assign-email">{selectedOption.email}</span>
              )}
            </div>
            <button
              type="button"
              className="hp-assign-remove"
              onClick={() => onChange(undefined)}
              aria-label="Remove"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
